import React, { useEffect } from 'react'
import { TouchableOpacity } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { openSettings, requestNotifications } from 'react-native-permissions'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, IconName } from '@components/base'
import { InlineAlert, ScrollViewWithFade, TouchableSection } from '@src/components'
import { NavigationContainer } from '@src/screens/Common/containers'
import { remindersSelector } from '@src/selectors/settings'
import { MobileAppFeature, UiInlineAlert } from '@src/types'
import { ReminderKind } from '@src/types'
import { remindersConfigSelector } from '@src/selectors/app'
import { User } from '@src/utils'
import SettingsSection from '../UserSettings/SettingsSection'
import ToggleSetting from '../UserSettings/ToggleSetting'
import { useNotificationsPermissionsStatus } from '../../utils/hooks'
import { useAvailableReminders } from './hooks'

export const NotificationSettings = () => {
  const styles = useStyleSheet(themedStyles)
  const notificationAuthStatus = useNotificationsPermissionsStatus()
  const navigation = useNavigation()

  const dispatch = useDispatch()
  const availableReminders = useAvailableReminders()
  const hasGlucosePatternsNotifications = User.hasFeature(
    MobileAppFeature.GlucosePatternsNotifications,
  )

  const reminders = useSelector(remindersSelector)
  const remindersConfig = useSelector(remindersConfigSelector)

  const remindersWithConfig = reminders.map((reminder) => {
    const config =
      remindersConfig.find((reminderKind) => reminderKind.key === reminder.kind) ||
      ({} as ReminderKind)
    return { reminder, reminderConfig: config }
  })

  useEffect(() => {
    dispatch({ type: 'settings/fetchReminders' })
  }, [dispatch])

  const onEnableNotificationsPress = async () => {
    const notificationsResponse = await requestNotifications(['alert', 'badge', 'sound'])

    if (notificationsResponse.status === 'blocked') {
      openSettings('notifications')
    }
  }

  return (
    <NavigationContainer title="Notification Settings">
      <SafeAreaView edges={['bottom']} style={styles.container}>
        <ScrollViewWithFade>
          {notificationAuthStatus && notificationAuthStatus.disabled && (
            <TouchableOpacity
              accessibilityLabel="Enable notifications"
              activeOpacity={0.7}
              style={styles.alertContainer}
              onPress={onEnableNotificationsPress}
            >
              <InlineAlert
                category={UiInlineAlert.Warning}
                message="Push Notifications are currently disabled"
              />
            </TouchableOpacity>
          )}

          {hasGlucosePatternsNotifications && (
            <SettingsSection title="Notifications">
              <ToggleSetting title="Glucose Patterns Notifications" field="patternsNotifications" />
            </SettingsSection>
          )}
          <SettingsSection title="Reminders">
            {remindersWithConfig.map(({ reminder, reminderConfig }) => (
              <TouchableSection
                key={reminder.id}
                iconName={reminderConfig.icon as IconName}
                title={reminderConfig.title}
                showRightArrow
                onPress={() =>
                  navigation.navigate('EditReminderModal', { reminder, reminderConfig })
                }
              />
            ))}
          </SettingsSection>
        </ScrollViewWithFade>
        <Button
          type="primary"
          style={styles.button}
          size="block"
          disabled={availableReminders.length === 0}
          accessibilityLabel="Add reminder"
          onPress={() => navigation.navigate('AddReminderModal')}
        >
          {availableReminders.length !== 0 ? 'Add reminder' : 'No Reminders Left to Add'}
        </Button>
      </SafeAreaView>
    </NavigationContainer>
  )
}

const themedStyles = StyleService.create({
  container: {
    flex: 1,
    paddingBottom: 16,
    backgroundColor: 'theme.background',
  },
  alertContainer: {
    margin: 16,
  },
  button: {
    marginHorizontal: 16,
  },
})
